import React from 'react';
import TimeSelect, { BusinessHoursRange } from '../TimeSelect';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Close, Undo } from '@mui/icons-material';
import { colors, inputs, typography } from '../../theme/designSystem';

// Peças da série montada na tela (RN-118m), compartilhadas pelo agendamento e pelo bloqueio de
// agenda (RN-118n): dias da semana, gerador de datas e a linha de sessão editável.

export const WEEKDAYS_PT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const formatDatePt = (d: Date): string =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

export interface SeriesGeneratorInput {
  first: Date;
  /** daily | weekly | biweekly | monthly | monthlyweekday */
  recurrenceType: string;
  count: number;
  /** datas removidas (yyyy-MM-dd): puladas, e a série completa no fim */
  skipped: string[];
  /** dias da semana (0 = domingo); com mais de um, a série percorre os dias de cada semana */
  weekdays: number[];
}

/** Datas da série: diário pula fim de semana; semanal +7; quinzenal +14; mensal pelo dia do
 *  mês ou pela posição da semana (RN-118i). Com vários dias da semana (RN-118m), percorre os
 *  dias escolhidos de cada semana (quinzenal: semana sim, semana não) e a quantidade é o
 *  total de sessões. */
export const generateSeriesDates = ({ first, recurrenceType, count, skipped, weekdays }: SeriesGeneratorInput): Date[] => {
  const skippedSet = new Set(skipped);
  const dates: Date[] = [];
  if (weekdays.length > 1) {
    const step = recurrenceType === 'biweekly' ? 14 : 7;
    let weekStart = new Date(first.getFullYear(), first.getMonth(), first.getDate() - first.getDay());
    let weeks = count + skippedSet.size + 8;
    while (dates.length < count && weeks-- > 0) {
      for (const dow of [...weekdays].sort((a, b) => a - b)) {
        const day = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + dow);
        if (day < first || dates.length >= count) {
          continue;
        }
        if (!skippedSet.has(toIsoDate(day))) {
          dates.push(day);
        }
      }
      weekStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + step);
    }
    return dates;
  }
  if (!skippedSet.has(toIsoDate(first))) {
    dates.push(new Date(first));
  }
  const cursor = new Date(first);
  let guard = count + skippedSet.size + 8;
  while (dates.length < count && guard-- > 0) {
    if (recurrenceType === 'daily') {
      do {
        cursor.setDate(cursor.getDate() + 1);
      } while (cursor.getDay() === 0 || cursor.getDay() === 6);
    } else if (recurrenceType === 'biweekly') {
      cursor.setDate(cursor.getDate() + 14);
    } else if (recurrenceType === 'monthly') {
      // Todo dia N da data inicial; mês sem o dia N usa o último (31 -> 30, fev -> 28/29)
      const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const daysInMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
      cursor.setFullYear(next.getFullYear(), next.getMonth(), Math.min(first.getDate(), daysInMonth));
    } else if (recurrenceType === 'monthlyweekday') {
      // Ordinal da âncora (1ª..5ª) no dia da semana; mês seguinte sem a 5ª usa a última
      const ordinal = Math.floor((first.getDate() - 1) / 7) + 1;
      const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const offset = (first.getDay() - next.getDay() + 7) % 7;
      let day = 1 + offset + (ordinal - 1) * 7;
      const daysInMonth = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
      while (day > daysInMonth) {
        day -= 7;
      }
      cursor.setFullYear(next.getFullYear(), next.getMonth(), day);
    } else {
      cursor.setDate(cursor.getDate() + 7);
    }
    if (!skippedSet.has(toIsoDate(cursor))) {
      dates.push(new Date(cursor));
    }
  }
  return dates;
};

export const toMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

export const fromMinutes = (total: number): string => {
  const capped = Math.min(Math.max(total, 0), 23 * 60 + 59);
  return `${String(Math.floor(capped / 60)).padStart(2, '0')}:${String(capped % 60).padStart(2, '0')}`;
};

export interface SeriesRow {
  /** data que o gerador produziu (chave estável dos ajustes e remoções) */
  key: string;
  index: number;
  date: string;
  startTime: string;
  endTime: string;
  adjusted: boolean;
}

export type SeriesEdits = Record<string, { date?: string; startTime?: string }>;

/** Linhas da série como vão para o servidor: data gerada ou ajustada à mão, horário do topo
 *  ou ajustado; o fim preserva a duração do topo. A 1ª linha é sempre o topo. */
export const buildSeriesRows = (
  dates: Date[], edits: SeriesEdits, topStart: string, duration: number): SeriesRow[] =>
  dates.map((d, index) => {
    const key = toIsoDate(d);
    const edit = index === 0 ? undefined : edits[key];
    const startTime = edit?.startTime ?? topStart;
    return {
      key,
      index,
      date: edit?.date ?? key,
      startTime,
      endTime: startTime ? fromMinutes(toMinutes(startTime) + duration) : '',
      adjusted: !!edit,
    };
  });

/** Motivo de cada linha inválida (chave → texto): vazia, antes da primeira, passando da
 *  meia-noite ou sobreposta a outra. `noun` nomeia o item no recado ("sessão", "bloqueio"). */
export const validateSeriesRows = (
  rows: SeriesRow[], startIso: string, topStart: string, duration: number, noun: string): Record<string, string> => {
  const errors: Record<string, string> = {};
  rows.forEach(row => {
    if (!row.date || !row.startTime) {
      errors[row.key] = 'Informe a data e o horário.';
    } else if (row.index > 0 && (row.date < startIso || (row.date === startIso && row.startTime < topStart))) {
      errors[row.key] = `Fica antes da primeira ${noun}.`;
    } else if (toMinutes(row.startTime) + duration > 24 * 60 - 1) {
      errors[row.key] = 'Passa da meia-noite.';
    } else {
      const clash = rows.find(other => other !== row && other.date === row.date
        && other.startTime < row.endTime && row.startTime < other.endTime);
      if (clash) {
        errors[row.key] = `Conflita com a ${noun} ${clash.index + 1}.`;
      }
    }
  });
  return errors;
};

export const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

// Campo compacto da lista de sessões: mesma borda/foco dos campos do modal, altura menor
// para caber data + horário na linha sem virar formulário dentro do formulário
export const compactFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 34,
    fontSize: '0.85rem',
    backgroundColor: colors.white,
    '& fieldset': { borderColor: inputs.default.borderColor },
    '&:hover fieldset': { borderColor: inputs.default.hover.borderColor },
    '&.Mui-focused fieldset': {
      borderColor: inputs.default.focus.borderColor,
      boxShadow: inputs.default.focus.boxShadow,
    },
    '&.Mui-disabled': { backgroundColor: colors.disabledBackground },
    '&.Mui-disabled input': { WebkitTextFillColor: colors.disabledText },
  },
  '& input': { padding: '0 0.5rem', fontVariantNumeric: 'tabular-nums' },
  '& input::-webkit-calendar-picker-indicator': { marginLeft: '4px', cursor: 'pointer' },
} as const;

// Caixa rolável da lista de sessões e ações de texto do cabeçalho (criação e edição)
export const seriesListSx = {
  maxHeight: 300,
  overflowY: 'auto',
  border: `1px solid ${colors.backgroundAlt}`,
  borderRadius: '6px',
  padding: '0.25rem 0.5rem',
} as const;

export const seriesLinkSx = {
  padding: '0 0.25rem',
  minWidth: 0,
  fontSize: '0.78rem',
  textTransform: 'none',
  color: colors.primary,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline', color: colors.primaryHover },
} as const;

export interface SeriesSlotRowProps {
  number: number;
  date: string;
  startTime: string;
  endTime: string;
  /** min do seletor de data (yyyy-MM-dd) */
  minDate?: string;
  editable: boolean;
  /** motivo de a linha não ser editável, exibido no lugar dos campos */
  lockedReason?: string;
  adjusted?: boolean;
  removed?: boolean;
  current?: boolean;
  holiday?: string;
  /** bloqueio de agenda no horário da linha (RN-118n) — aviso, não impede */
  blocked?: string;
  /** outro agendamento do profissional ou do paciente no horário — aviso, não impede */
  conflict?: string;
  error?: string;
  onDateChange?: (value: string) => void;
  onTimeChange?: (value: string) => void;
  /** Horário de atendimento da clínica: recorta a lista de horários */
  businessHours?: BusinessHoursRange[] | null;
  onRemove?: () => void;
  onRestore?: () => void;
  /** ação extra à direita (ex.: abrir a ocorrência na edição) */
  trailing?: React.ReactNode;
}

/** Linha da lista de sessões da série (RN-118m): número, dia da semana, data e horário
 *  editáveis, fim calculado (a duração é preservada) e remoção. No celular quebra em duas
 *  linhas — identificação em cima, campos embaixo — sem esconder nada atrás de hover. */
export const SeriesSlotRow: React.FC<SeriesSlotRowProps> = ({
  number, date, startTime, endTime, minDate, editable, lockedReason, adjusted, removed,
  current, holiday, blocked, conflict, error, onDateChange, onTimeChange, onRemove, onRestore, trailing,
  businessHours,
}) => {
  const day = date ? new Date(`${date}T00:00:00`) : null;
  const weekday = day && !Number.isNaN(day.getTime()) ? WEEKDAYS_PT[day.getDay()] : '';
  const dateLabel = day && !Number.isNaN(day.getTime()) ? formatDatePt(day) : '—';
  const muted = removed ? colors.textMuted : colors.textPrimary;
  return (
    <Box
      sx={{
        display: 'grid',
        alignItems: 'center',
        columnGap: '0.4rem',
        rowGap: '0.35rem',
        // Linha única só a partir de md: abaixo disso a coluna do modal é estreita demais e a
        // linha quebra em duas (identificação em cima, campos embaixo), como no celular
        // O seletor nativo precisa de ~145px (dd/mm/aaaa + ícone) e ~130px (hh:mm + ícone) para
        // mostrar o valor inteiro (medido no Chrome, 30/09). Lado a lado só cabe a partir de md;
        // abaixo, a data ocupa a linha e o horário desce ao lado do fim
        gridTemplateColumns: { xs: '1.5rem 2.25rem 1fr auto', md: '1.5rem 2.25rem minmax(9.5rem, 1fr) 8.5rem 4.25rem 1.75rem' },
        gridTemplateAreas: {
          xs: `"num dow . act" "date date date date" "time time time end" "note note note note"`,
          md: `"num dow date time end act" ". . note note note note"`,
        },
        padding: '0.4rem 0.25rem',
        borderRadius: '6px',
        backgroundColor: adjusted && !removed ? colors.primaryLight : 'transparent',
        transition: 'background-color 180ms ease-out',
        '& + &': { borderTop: `1px solid ${colors.backgroundSubtle}` },
      }}
    >
      <Typography sx={{ gridArea: 'num', fontSize: '0.8rem', color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
        {`${number}.`}
      </Typography>
      <Typography sx={{
        gridArea: 'dow',
        fontSize: '0.85rem',
        fontWeight: typography.fontWeight.medium,
        color: muted,
        textDecoration: removed ? 'line-through' : undefined,
      }}>
        {weekday}
      </Typography>

      {editable && !removed ? (
        <>
          <TextField
            type="date"
            size="small"
            value={date}
            onChange={(e) => onDateChange?.(e.target.value)}
            error={!!error}
            inputProps={{ min: minDate, 'aria-label': `Data da sessão ${number}` }}
            sx={{ ...compactFieldSx, gridArea: 'date', minWidth: 0 }}
          />
          <TimeSelect
            size="small"
            value={startTime}
            businessHours={businessHours}
            onChange={(v) => onTimeChange?.(v)}
            error={!!error}
            inputAriaLabel={`Horário inicial da sessão ${number}`}
            sx={{ ...compactFieldSx, gridArea: 'time', minWidth: 0 }}
          />
        </>
      ) : (
        <Typography sx={{
          // Ocupa as duas áreas dos campos (data e horário) nos dois layouts
          gridArea: 'date / date / time / time',
          fontSize: '0.9rem',
          color: muted,
          fontWeight: current ? typography.fontWeight.semibold : undefined,
          textDecoration: removed ? 'line-through' : undefined,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {`${dateLabel} ${startTime}`}
          {current ? ' (esta)' : ''}
        </Typography>
      )}

      <Typography sx={{
        gridArea: 'end',
        fontSize: '0.8rem',
        color: colors.textSecondary,
        fontVariantNumeric: 'tabular-nums',
        textDecoration: removed ? 'line-through' : undefined,
        whiteSpace: 'nowrap',
        justifySelf: 'start',
      }}>
        {endTime ? `até ${endTime}` : ''}
      </Typography>

      <Box sx={{ gridArea: 'act', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.125rem' }}>
        {trailing}
        {removed && onRestore ? (
          <Tooltip title="Desfazer a remoção" placement="top" enterTouchDelay={300}>
            <IconButton size="small" onClick={onRestore} aria-label={`Desfazer a remoção da sessão ${number}`}
              sx={{ padding: '4px', color: colors.primary }}>
              <Undo sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        ) : onRemove ? (
          <Tooltip title="Remover esta sessão" placement="top" enterTouchDelay={300}>
            <IconButton size="small" onClick={onRemove} aria-label={`Remover a sessão ${number}`}
              sx={{ padding: '4px', color: colors.textSecondary, '&:hover': { color: colors.error } }}>
              <Close sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {(error || holiday || blocked || conflict || lockedReason || removed) && (
        <Typography sx={{
          gridArea: 'note',
          fontSize: '0.78rem',
          lineHeight: 1.35,
          color: error ? colors.error : (holiday || blocked || conflict) && !removed ? '#8a5300' : colors.textSecondary,
        }}>
          {error
            ?? (removed
              ? 'Será removida ao aplicar.'
              : [conflict && `Conflito: ${conflict}`, holiday && `Feriado: ${holiday}`, blocked && `Horário bloqueado: ${blocked}`]
                .filter(Boolean).join(' · ')
                || lockedReason)}
        </Typography>
      )}
    </Box>
  );
};

interface WeekdayPickerProps {
  value: number[];
  /** dia fixo (o da data inicial): sempre marcado, não desmarca */
  anchor: number | null;
  onToggle: (dow: number) => void;
  label?: string;
}

/** Dias da semana da série: botões alternáveis, o dia da data inicial fixo. */
export const WeekdayPicker: React.FC<WeekdayPickerProps> = ({ value, anchor, onToggle, label = 'Dias da semana' }) => {
  const labelId = React.useId();
  return (
    <Box role="group" aria-labelledby={labelId}>
      <Typography
        id={labelId}
        sx={{ fontSize: '0.9rem', fontWeight: typography.fontWeight.medium, color: colors.textPrimary, marginBottom: '0.4rem' }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
        {WEEKDAYS_PT.map((dayLabel, dow) => {
          const selected = value.includes(dow);
          const fixed = dow === anchor;
          return (
            <Tooltip
              key={dayLabel}
              title={fixed ? 'Dia da data inicial: acompanha o campo Data' : ''}
              placement="top"
              enterTouchDelay={300}
            >
              <Box
                component="button"
                type="button"
                aria-pressed={selected}
                aria-label={WEEKDAY_NAMES[dow]}
                aria-disabled={fixed || undefined}
                onClick={() => { if (!fixed) onToggle(dow); }}
                sx={{
                  minWidth: 44,
                  height: 32,
                  padding: '0 0.625rem',
                  borderRadius: '999px',
                  border: `1px solid ${selected ? colors.primary : colors.border}`,
                  backgroundColor: selected ? colors.primary : colors.white,
                  color: selected ? colors.white : colors.textPrimary,
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  fontWeight: selected ? typography.fontWeight.semibold : typography.fontWeight.medium,
                  cursor: fixed ? 'default' : 'pointer',
                  transition: 'background-color 160ms ease-out, border-color 160ms ease-out, color 160ms ease-out',
                  '&:hover': fixed ? undefined : {
                    borderColor: colors.primary,
                    backgroundColor: selected ? colors.primaryHover : colors.primaryLight,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${colors.primary}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {dayLabel}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};
